import { Globe, LayoutDashboard, Trash2, UserRound } from 'lucide-react';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ListCard } from '@/_shared/components/list/ListCard';
import { MetaChip } from '@/_shared/components/list/MetaChip';
import { Badge } from '@/_shared/components/ui/badge';
import { timeAgo } from '@/_shared/lib/format';

import { type DashboardListViewItem, panelCountLabel } from '../lib/dashboardList';
import { StarToggleButton } from './StarToggleButton';

interface DashboardCardProps {
  dashboard: DashboardListViewItem;
  /** Destination href for the whole card (built with `wp()`). */
  to: string;
  onDelete: (id: string) => void;
  /** Challenge-mode creator name, or null when attribution is off. */
  creatorName: string | null;
  onToggleStar: (id: string, nextStarred: boolean) => void;
  starPending?: boolean;
}

export function DashboardCard({
  dashboard,
  to,
  onDelete,
  creatorName,
  onToggleStar,
  starPending = false,
}: DashboardCardProps): React.ReactElement {
  return (
    <ListCard
      to={to}
      title={dashboard.title}
      badges={
        <>
          <NeedsRepairBadge listRow state={dashboard.accountRepair.state} className="shrink-0" />
          {dashboard.isPublic && (
            <Badge variant="info" bordered className="shrink-0">
              <Globe className="size-3" />
              Public
            </Badge>
          )}
        </>
      }
      actions={
        <div className="flex items-center gap-1">
          <StarToggleButton
            dashboardTitle={dashboard.title}
            starred={dashboard.isStarred}
            loading={starPending}
            onToggle={() => onToggleStar(dashboard.id, !dashboard.isStarred)}
          />
          <KebabMenu
            label={`Actions for ${dashboard.title}`}
            actions={[{ label: 'Delete', icon: Trash2, destructive: true, onSelect: () => onDelete(dashboard.id) }]}
          />
        </div>
      }
      description={dashboard.description}
      // Panel count + creator sit together on the footer's left, with "updated" on the right — so the
      // card's bottom line reads "what it is · who made it" against "when it last changed".
      footerLeft={
        <>
          <MetaChip icon={LayoutDashboard}>{panelCountLabel(dashboard.spec.panels.length)}</MetaChip>
          {creatorName && <MetaChip icon={UserRound}>{creatorName}</MetaChip>}
        </>
      }
      footerRight={`Updated ${timeAgo(dashboard.updatedAt)}`}
    />
  );
}
